import { useEffect, useState, type ReactNode } from 'react'
import { getSystemConfig, updateSystemConfig, type SystemConfig as Cfg, type ConfigUpdate } from '../api/monitoring'
import { useUI } from '../store/ui'

const SANDBOX_URL = 'https://sandbox.safaricom.co.ke'
const PROD_URL = 'https://api.safaricom.co.ke'

export function SystemConfig() {
  const [cfg, setCfg] = useState<Cfg | null>(null)
  const [err, setErr] = useState('')

  async function load() {
    setErr('')
    try {
      setCfg(await getSystemConfig())
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load config')
    }
  }
  useEffect(() => {
    void load()
  }, [])

  return (
    <div className="page">
      <div className="ph">
        <div>
          <h1>System config</h1>
          <div className="desc">The effective runtime configuration. Secrets are masked; the editable settings below are stored server-side (secrets encrypted). Fields marked “restart” apply on the next server start.</div>
        </div>
      </div>

      {err && <div className="auth-error mt8">{err}</div>}

      {cfg && cfg.warnings && cfg.warnings.length > 0 && (
        <div className="card pad mt8" style={{ borderColor: 'var(--warning)' }}>
          <div className="card-title" style={{ color: 'var(--warning)' }}>Attention</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.5 }}>
            {cfg.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {cfg && <EditSections cfg={cfg} onSaved={setCfg} />}

      {/* Read-only effective snapshot */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 'var(--gap)', marginTop: 'var(--gap)' }}>
        {cfg === null &&
          [0, 1, 2, 3].map((i) => (
            <div className="card pad" key={i}>
              <div className="skeleton" style={{ width: '40%', height: 14, marginBottom: 12 }} />
              {[0, 1, 2].map((j) => (
                <div key={j} className="skeleton" style={{ width: '80%', height: 12, marginBottom: 8 }} />
              ))}
            </div>
          ))}
        {cfg?.groups.map((g) => (
          <div className="card pad" key={g.title}>
            <div className="card-title">{g.title}</div>
            <div className="kv">
              {g.items.map((it) => (
                <div key={it.label} style={{ display: 'contents' }}>
                  <div className="k">{it.label}</div>
                  <div style={it.tone === 'warn' ? { color: 'var(--warning)', fontWeight: 600 } : undefined}>{it.value}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---- editable sections ------------------------------------------------------

function EditSections({ cfg, onSaved }: { cfg: Cfg; onSaved: (c: Cfg) => void }) {
  const restart = new Set(cfg.restart_keys)
  const overrides = new Set(cfg.overrides)
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 'var(--gap)', marginTop: 8 }}>
      <MpesaSection cfg={cfg} onSaved={onSaved} restart={restart} overrides={overrides} />
      <EmailSection cfg={cfg} onSaved={onSaved} restart={restart} overrides={overrides} />
      <EventBusSection cfg={cfg} onSaved={onSaved} restart={restart} overrides={overrides} />
      <SecuritySection cfg={cfg} onSaved={onSaved} restart={restart} overrides={overrides} />
    </div>
  )
}

type SectionProps = { cfg: Cfg; onSaved: (c: Cfg) => void; restart: Set<string>; overrides: Set<string> }

// useSaver returns a submit helper that PATCHes a ConfigUpdate, toasts, and lifts the fresh config up.
function useSaver(onSaved: (c: Cfg) => void) {
  const showToast = useUI((s) => s.showToast)
  const [busy, setBusy] = useState(false)
  async function save(patch: ConfigUpdate, after?: () => void) {
    if (Object.keys(patch).length === 0) {
      showToast('No changes to save', 'info')
      return
    }
    setBusy(true)
    try {
      const next = await updateSystemConfig(patch)
      onSaved(next)
      after?.()
      showToast('Settings saved', 'success')
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Save failed', 'danger')
    } finally {
      setBusy(false)
    }
  }
  return { save, busy }
}

// Field label with optional restart / override badges.
function FieldLabel({ text, k, restart, overrides }: { text: string; k?: string; restart?: Set<string>; overrides?: Set<string> }) {
  return (
    <label>
      {text}
      {k && overrides?.has(k) && <span className="muted" style={{ fontSize: 10.5, fontWeight: 600 }}> · override</span>}
      {k && restart?.has(k) && <span style={{ fontSize: 10.5, color: 'var(--warning)', fontWeight: 600 }}> · restart</span>}
    </label>
  )
}

function Text({ label, value, onChange, k, restart, overrides, placeholder, aria }: { label: string; value: string; onChange: (v: string) => void; k?: string; restart?: Set<string>; overrides?: Set<string>; placeholder?: string; aria?: string }) {
  return (
    <div className="field">
      <FieldLabel text={label} k={k} restart={restart} overrides={overrides} />
      <input className="input" aria-label={aria ?? label} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  )
}

function Secret({ label, isSet, value, onChange, k, restart, aria }: { label: string; isSet: boolean; value: string; onChange: (v: string) => void; k?: string; restart?: Set<string>; aria?: string }) {
  return (
    <div className="field">
      <label>
        {label}
        {isSet ? <span className="muted" style={{ fontSize: 10.5 }}> · set</span> : <span style={{ fontSize: 10.5, color: 'var(--warning)' }}> · not set</span>}
        {k && restart?.has(k) && <span style={{ fontSize: 10.5, color: 'var(--warning)', fontWeight: 600 }}> · restart</span>}
      </label>
      <input className="input" type="password" aria-label={aria ?? label} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Leave blank to keep current" />
    </div>
  )
}

function Toggle({ label, checked, onChange, k, restart, overrides }: { label: string; checked: boolean; onChange: (v: boolean) => void; k?: string; restart?: Set<string>; overrides?: Set<string> }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, marginBottom: 13, cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
      {k && overrides?.has(k) && <span className="muted" style={{ fontSize: 10.5, fontWeight: 600 }}>· override</span>}
      {k && restart?.has(k) && <span style={{ fontSize: 10.5, color: 'var(--warning)', fontWeight: 600 }}>· restart</span>}
    </label>
  )
}

function SaveBar({ busy, onSave, note }: { busy: boolean; onSave: () => void; note?: ReactNode }) {
  return (
    <div style={{ marginTop: 6 }}>
      <button className="btn primary" disabled={busy} onClick={onSave}>
        {busy ? 'Saving…' : 'Save'}
      </button>
      {note && <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>{note}</div>}
    </div>
  )
}

// diff helpers: include a field only when it actually changed from the loaded baseline.
function changed<T>(patch: Record<string, unknown>, key: string, cur: T, base: T) {
  if (cur !== base) patch[key] = cur
}

// ---- M-Pesa ----
function MpesaSection({ cfg, onSaved, restart, overrides }: SectionProps) {
  const e = cfg.editable
  const { save, busy } = useSaver(onSaved)
  const [publicBase, setPublicBase] = useState(e.public_base_url)
  const [mpesaUrl, setMpesaUrl] = useState(e.mpesa_url)
  const [stuck, setStuck] = useState(String(e.stuck_payout_minutes))
  const [paused, setPaused] = useState(e.money_out_paused)
  const [callbackPass, setCallbackPass] = useState('')
  const [cert, setCert] = useState('') // PEM text from file upload
  const [certName, setCertName] = useState('')
  const darajaChoice = mpesaUrl === SANDBOX_URL ? 'sandbox' : mpesaUrl === PROD_URL ? 'production' : 'custom'

  function submit() {
    const patch: ConfigUpdate = {}
    changed(patch, 'public_base_url', publicBase.trim(), e.public_base_url)
    changed(patch, 'mpesa_url', mpesaUrl.trim(), e.mpesa_url)
    changed(patch, 'stuck_payout_minutes', Number(stuck), e.stuck_payout_minutes)
    changed(patch, 'money_out_paused', paused, e.money_out_paused)
    if (callbackPass.trim()) patch.mpesa_callback_password = callbackPass.trim()
    if (cert.trim()) patch.mpesa_cert = cert.trim()
    void save(patch, () => {
      setCallbackPass('')
      setCert('')
      setCertName('')
    })
  }

  return (
    <div className="card pad">
      <div className="card-title">M-Pesa / Daraja</div>
      <Text label="Public base URL" value={publicBase} onChange={setPublicBase} k="public_base_url" restart={restart} overrides={overrides} placeholder="https://abc123.ngrok.io" />
      <div className="field">
        <FieldLabel text="Daraja base" k="mpesa_url" restart={restart} overrides={overrides} />
        <select className="select" aria-label="Daraja base" value={darajaChoice} onChange={(ev) => { const v = ev.target.value; if (v === 'sandbox') setMpesaUrl(SANDBOX_URL); else if (v === 'production') setMpesaUrl(PROD_URL) }}>
          <option value="sandbox">Sandbox — {SANDBOX_URL}</option>
          <option value="production">Production — {PROD_URL}</option>
          {darajaChoice === 'custom' && <option value="custom">Custom — {mpesaUrl}</option>}
        </select>
      </div>
      <Text label="Reconcile stuck payout after (minutes)" value={stuck} onChange={setStuck} k="stuck_payout_minutes" restart={restart} overrides={overrides} aria="Reconcile stuck payout after minutes" />
      <Toggle label="Pause money-out dispatch (holds payouts; reconcile + event relay keep running)" checked={paused} onChange={setPaused} k="money_out_paused" restart={restart} overrides={overrides} />
      <Secret label="Callback password" isSet={e.callback_pass_set} value={callbackPass} onChange={setCallbackPass} aria="Callback password" />
      <div className="field">
        <label>
          Security certificate (.cer / PEM)
          {e.mpesa_cert_set ? <span className="muted" style={{ fontSize: 10.5 }}> · set</span> : <span className="muted" style={{ fontSize: 10.5 }}> · not set</span>}
        </label>
        <input
          type="file"
          aria-label="Security certificate"
          accept=".cer,.pem,.crt,.txt"
          onChange={async (ev) => {
            const f = ev.target.files?.[0]
            if (!f) return
            setCert(await f.text())
            setCertName(f.name)
          }}
        />
        {certName && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Selected: {certName}</div>}
      </div>
      <SaveBar busy={busy} onSave={submit} note="Changing the callback password requires re-registering C2B URLs (Gateways → Register URLs)." />
    </div>
  )
}

// ---- Email ----
function EmailSection({ cfg, onSaved, restart, overrides }: SectionProps) {
  const e = cfg.editable
  const { save, busy } = useSaver(onSaved)
  const [host, setHost] = useState(e.smtp_host)
  const [port, setPort] = useState(e.smtp_port)
  const [username, setUsername] = useState(e.smtp_username)
  const [password, setPassword] = useState('')
  const [from, setFrom] = useState(e.mail_from)
  const [tagline, setTagline] = useState(e.mail_tagline)
  const [support, setSupport] = useState(e.mail_support_email)

  function submit() {
    const patch: ConfigUpdate = {}
    changed(patch, 'smtp_host', host.trim(), e.smtp_host)
    changed(patch, 'smtp_port', port.trim(), e.smtp_port)
    changed(patch, 'smtp_username', username.trim(), e.smtp_username)
    changed(patch, 'mail_from', from.trim(), e.mail_from)
    changed(patch, 'mail_tagline', tagline.trim(), e.mail_tagline)
    changed(patch, 'mail_support_email', support.trim(), e.mail_support_email)
    if (password.trim()) patch.smtp_password = password.trim()
    void save(patch, () => setPassword(''))
  }

  return (
    <div className="card pad">
      <div className="card-title">Email (SMTP)</div>
      <Text label="SMTP host" value={host} onChange={setHost} k="smtp_host" restart={restart} overrides={overrides} placeholder="smtp.example.com" />
      <Text label="SMTP port" value={port} onChange={setPort} k="smtp_port" restart={restart} overrides={overrides} placeholder="587" />
      <Text label="SMTP username" value={username} onChange={setUsername} k="smtp_username" restart={restart} overrides={overrides} placeholder="(blank = no auth)" />
      <Secret label="SMTP password" isSet={e.smtp_password_set} value={password} onChange={setPassword} k="smtp_password" restart={restart} aria="SMTP password" />
      <Text label="From address" value={from} onChange={setFrom} k="mail_from" restart={restart} overrides={overrides} placeholder="no-reply@fund.co.ke" />
      <Text label="Email tagline" value={tagline} onChange={setTagline} k="mail_tagline" restart={restart} overrides={overrides} />
      <Text label="Support email" value={support} onChange={setSupport} k="mail_support_email" restart={restart} overrides={overrides} />
      <SaveBar busy={busy} onSave={submit} note="SMTP changes take effect on the next server restart." />
    </div>
  )
}

// ---- Event bus ----
function EventBusSection({ cfg, onSaved, restart, overrides }: SectionProps) {
  const e = cfg.editable
  const { save, busy } = useSaver(onSaved)
  const [relay, setRelay] = useState(e.event_relay)
  const [host, setHost] = useState(e.rabbitmq_host)
  const [port, setPort] = useState(e.rabbitmq_port)
  const [username, setUsername] = useState(e.rabbitmq_username)
  const [password, setPassword] = useState('')
  const [tls, setTls] = useState(e.rabbitmq_tls)
  const [exchange, setExchange] = useState(e.rabbitmq_exchange)
  const [queue, setQueue] = useState(e.event_queue)
  const [prefix, setPrefix] = useState(e.event_routing_prefix)
  const [source, setSource] = useState(e.event_source)

  function submit() {
    const patch: ConfigUpdate = {}
    changed(patch, 'event_relay', relay, e.event_relay)
    changed(patch, 'rabbitmq_host', host.trim(), e.rabbitmq_host)
    changed(patch, 'rabbitmq_port', port.trim(), e.rabbitmq_port)
    changed(patch, 'rabbitmq_username', username.trim(), e.rabbitmq_username)
    changed(patch, 'rabbitmq_tls', tls, e.rabbitmq_tls)
    changed(patch, 'rabbitmq_exchange', exchange.trim(), e.rabbitmq_exchange)
    changed(patch, 'event_queue', queue.trim(), e.event_queue)
    changed(patch, 'event_routing_prefix', prefix.trim(), e.event_routing_prefix)
    changed(patch, 'event_source', source.trim(), e.event_source)
    if (password.trim()) patch.rabbitmq_password = password.trim()
    void save(patch, () => setPassword(''))
  }

  return (
    <div className="card pad">
      <div className="card-title">Event bus (RabbitMQ)</div>
      <Toggle label="Relay enabled (publish reversal/refund events)" checked={relay} onChange={setRelay} k="event_relay" restart={restart} overrides={overrides} />
      <Text label="RabbitMQ host" value={host} onChange={setHost} k="rabbitmq_host" restart={restart} overrides={overrides} placeholder="localhost" />
      <Text label="RabbitMQ port" value={port} onChange={setPort} k="rabbitmq_port" restart={restart} overrides={overrides} placeholder="5672" />
      <Text label="Username" value={username} onChange={setUsername} k="rabbitmq_username" restart={restart} overrides={overrides} aria="RabbitMQ username" />
      <Secret label="Password" isSet={e.rabbitmq_password_set} value={password} onChange={setPassword} k="rabbitmq_password" restart={restart} aria="RabbitMQ password" />
      <Toggle label="TLS (amqps)" checked={tls} onChange={setTls} k="rabbitmq_tls" restart={restart} overrides={overrides} />
      <Text label="Exchange" value={exchange} onChange={setExchange} k="rabbitmq_exchange" restart={restart} overrides={overrides} placeholder="amq.topic" />
      <Text label="Event queue" value={queue} onChange={setQueue} k="event_queue" restart={restart} overrides={overrides} placeholder="payments-events" />
      <Text label="Routing prefix (optional)" value={prefix} onChange={setPrefix} k="event_routing_prefix" restart={restart} overrides={overrides} placeholder="(none)" />
      <Text label="Envelope source" value={source} onChange={setSource} k="event_source" restart={restart} overrides={overrides} placeholder="Payments Console" />
      <SaveBar busy={busy} onSave={submit} note="Broker changes take effect on the next server restart." />
    </div>
  )
}

// ---- Security ----
function SecuritySection({ cfg, onSaved, restart, overrides }: SectionProps) {
  const e = cfg.editable
  const { save, busy } = useSaver(onSaved)
  const [maxAttempts, setMaxAttempts] = useState(String(e.login_max_attempts))
  const [lockMin, setLockMin] = useState(String(e.login_lock_minutes))
  const [otpEnabled, setOtpEnabled] = useState(e.email_otp_enabled)
  const [otpTtl, setOtpTtl] = useState(String(e.email_otp_ttl_minutes))
  const [otpMax, setOtpMax] = useState(String(e.otp_max_attempts))
  const [accessTtl, setAccessTtl] = useState(e.access_ttl)
  const [refreshTtl, setRefreshTtl] = useState(e.refresh_ttl)
  const [sessionIdle, setSessionIdle] = useState(e.session_idle_timeout)
  const [sessionMaxLife, setSessionMaxLife] = useState(e.session_max_lifetime)

  function submit() {
    const patch: ConfigUpdate = {}
    changed(patch, 'login_max_attempts', Number(maxAttempts), e.login_max_attempts)
    changed(patch, 'login_lock_minutes', Number(lockMin), e.login_lock_minutes)
    changed(patch, 'email_otp_enabled', otpEnabled, e.email_otp_enabled)
    changed(patch, 'email_otp_ttl_minutes', Number(otpTtl), e.email_otp_ttl_minutes)
    changed(patch, 'otp_max_attempts', Number(otpMax), e.otp_max_attempts)
    changed(patch, 'access_ttl', accessTtl.trim(), e.access_ttl)
    changed(patch, 'refresh_ttl', refreshTtl.trim(), e.refresh_ttl)
    changed(patch, 'session_idle_timeout', sessionIdle.trim(), e.session_idle_timeout)
    changed(patch, 'session_max_lifetime', sessionMaxLife.trim(), e.session_max_lifetime)
    void save(patch)
  }

  return (
    <div className="card pad">
      <div className="card-title">Security</div>
      <Text label="Login max attempts" value={maxAttempts} onChange={setMaxAttempts} k="login_max_attempts" restart={restart} overrides={overrides} />
      <Text label="Lockout (minutes)" value={lockMin} onChange={setLockMin} k="login_lock_minutes" restart={restart} overrides={overrides} />
      <Toggle label="Email OTP enabled" checked={otpEnabled} onChange={setOtpEnabled} k="email_otp_enabled" restart={restart} overrides={overrides} />
      <Text label="OTP TTL (minutes)" value={otpTtl} onChange={setOtpTtl} k="email_otp_ttl_minutes" restart={restart} overrides={overrides} />
      <Text label="OTP max attempts" value={otpMax} onChange={setOtpMax} k="otp_max_attempts" restart={restart} overrides={overrides} />
      <Text label="Access token TTL" value={accessTtl} onChange={setAccessTtl} k="access_ttl" restart={restart} overrides={overrides} placeholder="15m" />
      <Text label="Refresh token TTL" value={refreshTtl} onChange={setRefreshTtl} k="refresh_ttl" restart={restart} overrides={overrides} placeholder="720h" />
      <Text label="Session idle timeout" value={sessionIdle} onChange={setSessionIdle} k="session_idle_timeout" restart={restart} overrides={overrides} placeholder="30m" />
      <Text label="Session max lifetime" value={sessionMaxLife} onChange={setSessionMaxLife} k="session_max_lifetime" restart={restart} overrides={overrides} placeholder="12h" />
      <SaveBar busy={busy} onSave={submit} note="Token TTLs apply on the next server restart; session caps, login & OTP policy apply immediately (within ~30s)." />
    </div>
  )
}
